import { faImageCirclePlus } from "@fortawesome/duotone-regular-svg-icons";
import { faFlag } from "@fortawesome/duotone-regular-svg-icons/faFlag";
import { faSlash } from "@fortawesome/duotone-regular-svg-icons/faSlash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Anchor,
  Box,
  Flex,
  Group,
  Modal,
  Paper,
  ScrollArea,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { MapGeoJSONFeature } from "maplibre-gl";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useMap } from "react-map-gl/maplibre";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getCampgroundDetails } from "../../api-clients/iwannacamp-api";
import { useIsSmallScreen } from "../../hooks/breakpoints";
import useIdToken from "../../hooks/useIdToken";
import { TAGS_TO_ICONS } from "../../utils/amenityTags";
import { isDevMode } from "../../utils/debug";
import { getCampgroundHeroImageId } from "../../utils/images";
import { createLogger } from "../../utils/logger";
import { openLoginModal } from "../auth/useOpenLoginModal";
import {
  getCampgroundId,
  getCampgroundName,
} from "../campground-card/campgroundUtils";
import FavoriteCampgroundButton from "../campground-card/FavoriteCampgroundButton";
import {
  MeanPreciptationChart,
  MeanTemperatureChart,
} from "../campground-card/WeatherAverages";
import ImageUploadModal from "../campground-image-upload/ImageUploadModal";
import ReportIssueModal from "../reports/ReportIssueModal";
import ReviewsSection from "../reviews/ReviewsSection";
import { CampgroundImages } from "./CampgroundImages";
import { TableOfContentsTabs } from "./TableOfContentsTabs";

const logger = createLogger("CampgroundDetailsModal");

interface CampgroundDetailsModalProps {}

const CampgroundDetailsModal: FunctionComponent<
  CampgroundDetailsModalProps
> = () => {
  const [scrollAreaViewportRef, setScrollAreaViewportRef] =
    useState<HTMLDivElement | null>(null);
  const scrollAreaRefCallback = useCallback((node: HTMLDivElement | null) => {
    setScrollAreaViewportRef(node);
  }, []);

  const idToken = useIdToken();
  const { main: map } = useMap();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: campgroundIdParam } = useParams<{ id: string }>();
  const state = location.state as { campground?: MapGeoJSONFeature };
  const [campground, setCampground] = useState<MapGeoJSONFeature | undefined>(
    state?.campground
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [opened, setOpened] = useState(false);
  const [reportModalOpened, setReportModalOpened] = useState(false);
  const [uploadModalOpened, setUploadModalOpened] = useState(false);
  const isSmallScreen = useIsSmallScreen();

  // Fetch campground details if not provided via state (deeplink scenario)
  useEffect(() => {
    const fetchCampground = async () => {
      if (!campground && campgroundIdParam && map) {
        setLoading(true);
        setError(undefined);
        try {
          const response = await getCampgroundDetails({
            path: { campground_sqid: campgroundIdParam },
          });

          if (response.data) {
            // Convert CampgroundDetails to MapGeoJSONFeature format
            const feature: Partial<MapGeoJSONFeature> = {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [response.data.lon!, response.data.lat!],
              },
              properties: {
                sqid: response.data.sqid,
                name: response.data.name,
                amenity_count: response.data.amenity_count,
                average_rating: response.data.average_rating,
                review_count: response.data.review_count,
                ...response.data.tags,
              },
              id: response.data.id,
            };

            setCampground(feature as MapGeoJSONFeature);

            setTimeout(() => {
              // The map is loaded lazily, so on a deep link to a campground it
              // may not have mounted yet by the time this fires.
              if (!map) return;
              map.setCenter({
                lat: response.data.lat!,
                lon: response.data.lon!,
              });
              map.setZoom(10);
            }, 500);
          }
        } catch (err) {
          logger.error("Failed to fetch campground details:", err);
          setError("Failed to load campground details");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchCampground();
  }, [campgroundIdParam, campground, navigate, map]);

  useEffect(() => {
    if (!opened && campground) {
      setTimeout(() => setOpened(true), 0);
    }
  }, [campground, opened]);

  const loadingContent = (
    <Modal.Content mah={"100vh"} h="100vh" maw={"1080px"} radius={0}>
      <ScrollArea.Autosize mah={"100vh"} h="100vh">
        <Modal.Header>
          <Skeleton height={32} width="60%" />
          <Group>
            <Skeleton height={36} width={36} circle />
            <Skeleton height={36} width={36} circle />
          </Group>
        </Modal.Header>
        <Modal.Body>
          <Stack gap="md">
            {/* Hero image skeleton */}
            <Skeleton height="60vh" />

            {/* Amenities skeleton */}
            <Box>
              <Skeleton height={28} width={120} mb="sm" />
              <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
                {[...Array(8)].map((_, i) => (
                  <Skeleton key={i} height={60} />
                ))}
              </SimpleGrid>
            </Box>

            {/* Weather skeleton */}
            <Box>
              <Skeleton height={28} width={100} mb="sm" />
              <Stack gap="sm">
                <Skeleton height={200} />
                <Skeleton height={200} />
              </Stack>
            </Box>

            {/* Reviews skeleton */}
            <Box>
              <Skeleton height={28} width={100} mb="sm" />
              <Stack gap="sm">
                <Skeleton height={100} />
                <Skeleton height={100} />
              </Stack>
            </Box>
          </Stack>
        </Modal.Body>
      </ScrollArea.Autosize>
    </Modal.Content>
  );

  if (error) {
    return (
      <Modal.Root opened={true} onClose={() => navigate(-1)} size="sm">
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Error</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <Text c="red">{error}</Text>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    );
  }

  if (!campground) {
    return null;
  }

  const properties = campground.properties;
  if (!properties) {
    return null;
  }

  // Generate icons for amenities
  const amenityIcons = Object.entries(TAGS_TO_ICONS)
    .map(([tagName, iconDef]) => {
      if (properties[tagName] !== undefined) {
        const isAvailable =
          properties[tagName] != "no" && properties[tagName] != "unavailable";
        return (
          <Paper shadow="md" p="xs" withBorder radius="md" display={"flex"}>
            <Flex align={"center"}>
              <div className="tag-icon">
                {!isAvailable && (
                  <FontAwesomeIcon
                    icon={faSlash}
                    style={{
                      position: "absolute",
                      top: "2px",
                      left: "-2px",
                      fontSize: "2em",
                      fontWeight: "bold",
                      transform: "rotate(7deg)",
                    }}
                  />
                )}
                <FontAwesomeIcon icon={iconDef.icon} />
              </div>
              <Text size="xs" ml="sm">
                {(isAvailable ? "" : "no ") + iconDef.tooltip}
              </Text>
            </Flex>
          </Paper>
        );
      }
    })
    .filter((icon) => icon !== undefined);

  const campgroundName = getCampgroundName(campground);
  const campgroundId = getCampgroundId(campground);
  const campgroundImage = getCampgroundHeroImageId(campground);

  const handleReportClick = () => {
    if (!idToken) {
      openLoginModal(() => setReportModalOpened(true));
      return;
    }
    setReportModalOpened(true);
  };

  const handleUploadClick = () => {
    if (!idToken) {
      openLoginModal(() => setUploadModalOpened(true));
      return;
    }
    setUploadModalOpened(true);
  };

  return (
    <Modal.Root
      opened={opened}
      onClose={() => {
        if (location.state) {
          // State is set if the user navigated here from within the app
          navigate(-1);
        } else {
          navigate("/explore", { replace: true });
        }
      }}
      size={isSmallScreen ? "100vw" : "80vw"}
      styles={{ inner: { padding: 0 } }}
      transitionProps={{
        transition: "fade",
        duration: 200,
        timingFunction: "ease",
      }}
      zIndex={305}
      yOffset={0}
    >
      <Modal.Overlay />

      {loading ? (
        loadingContent
      ) : (
        <Modal.Content
          mah={"100vh"}
          h="100vh"
          maw={"1080px"}
          radius={0}
          id="campground-details-modal"
        >
          <ScrollArea.Autosize
            mah={"100vh"}
            h="100vh"
            viewportRef={scrollAreaRefCallback}
          >
            <Modal.Header>
              <Modal.Title fz={{ base: "h5", sm: "h3" }}>
                {campgroundName}
              </Modal.Title>
              <Group>
                <Tooltip label="report an issue">
                  <Anchor
                    left={0}
                    autoFocus={false}
                    onClick={handleReportClick}
                  >
                    <FontAwesomeIcon
                      icon={faFlag}
                      color="var(--mantine-color-red-4)"
                    />
                  </Anchor>
                </Tooltip>
                <Tooltip label="upload photo">
                  <Anchor
                    left={0}
                    autoFocus={false}
                    onClick={handleUploadClick}
                  >
                    <FontAwesomeIcon icon={faImageCirclePlus} color="grey" />
                  </Anchor>
                </Tooltip>
                <FavoriteCampgroundButton campgroundId={campgroundId} />
                <Modal.CloseButton style={{ marginInlineStart: "unset" }} />
              </Group>
            </Modal.Header>
            {scrollAreaViewportRef && (
              <TableOfContentsTabs
                scrollAreaViewportRef={scrollAreaViewportRef}
              />
            )}
            <Modal.Body>
              <Stack gap={"0.3rem"}>
                {campgroundImage && (
                  <Box maw="100%" h="60vh" pos="relative">
                    <Title order={2} mah={0} pos="absolute" top={-100} left={0}>
                      Photos
                    </Title>
                    <CampgroundImages
                      defaultImage={campgroundImage}
                      campgroundId={campgroundId}
                    />
                  </Box>
                )}
                <Flex gap={"sm"}>
                  <Flex flex={1} gap="sm" direction={"column"}>
                    {(isDevMode() ||
                      (amenityIcons && amenityIcons.length > 0)) && (
                      <Box>
                        <Title order={3}>Amenities</Title>
                        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={"xs"}>
                          {amenityIcons}
                        </SimpleGrid>
                      </Box>
                    )}
                    <Box>
                      <Title order={3}>Weather</Title>
                      <Stack gap="0.4rem" m="sm">
                        <Text size="xs" c="dimmed">
                          PRISM Monthly Mean Temperature (°F)
                        </Text>
                        <MeanTemperatureChart campground={campground} />
                        <Text size="xs" c="dimmed">
                          PRISM Monthly Mean Precipitation (inches)
                        </Text>
                        <MeanPreciptationChart campground={campground} />
                      </Stack>
                    </Box>
                    <Box>
                      <ReviewsSection
                        campgroundSqid={campgroundId}
                        averageRating={properties.average_rating || 0}
                        reviewCount={properties.review_count || 0}
                      />
                    </Box>
                  </Flex>
                </Flex>
              </Stack>
            </Modal.Body>
          </ScrollArea.Autosize>
        </Modal.Content>
      )}

      {/* Report Issue Modal */}
      <ReportIssueModal
        opened={reportModalOpened}
        onClose={() => setReportModalOpened(false)}
        campgroundSqid={campgroundId}
        campgroundName={campgroundName}
      />

      {/* Image Upload Modal */}
      <ImageUploadModal
        opened={uploadModalOpened}
        onClose={() => setUploadModalOpened(false)}
        campgroundSqid={campgroundId}
        campgroundName={campgroundName}
      />
    </Modal.Root>
  );
};

export default CampgroundDetailsModal;
